/**
 * /admin/tokens — org-wide bearer-token view for platform admins. Filter
 * by app / user / state, revoke any one, or bulk-revoke a user's tokens
 * across every app (offboarding flow).
 *
 * Mirrors the per-app manager view on /apps/:slug/tokens but spans every
 * app and adds bulk operations.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, apiMessage } from '../../api/client.js';
import { AdminTabs } from '../../components/AdminTabs.js';
import { IconPlug, IconSearch, IconTrash } from '../../components/Icons.js';
import {
  Banner,
  Button,
  Card,
  CardHeader,
  Mono,
  PageHeader,
  PageSkeleton,
  inputClass,
} from '../../components/ui.js';
import { relTime } from '../../lib/format.js';
import { runAction, type ActionMessage } from '../../lib/runAction.js';

interface AdminTokenRow {
  id: string;
  label: string;
  expires_at: number;
  last_used_at: number | null;
  revoked_at: number | null;
  revoked_by: string | null;
  revoked_reason: 'user' | 'manager' | 'admin' | 'cascade' | null;
  created_at: number;
  user_id: string;
  user_email: string;
  user_name: string | null;
  app_id: string;
  app_slug: string;
  app_name: string;
}

type StatusFilter = 'active' | 'revoked' | 'expiring' | 'all';

const STATUS_OPTIONS: { value: StatusFilter; label: string; description: string }[] = [
  { value: 'active', label: 'Active', description: 'Active tokens across every app.' },
  {
    value: 'expiring',
    label: 'Expiring within 7 days',
    description: 'Active tokens that expire within 7 days.',
  },
  {
    value: 'revoked',
    label: 'Revoked',
    description: 'Revoked tokens (retained for audit).',
  },
  { value: 'all', label: 'All', description: 'All tokens, including revoked + expired.' },
];

function statusDescription(status: StatusFilter): string {
  return STATUS_OPTIONS.find((o) => o.value === status)?.description ?? '';
}

interface State {
  loading: boolean;
  tokens?: AdminTokenRow[];
  error?: string;
}

export function AdminTokens() {
  const [state, setState] = useState<State>({ loading: true });
  const [filterText, setFilterText] = useState('');
  const [status, setStatus] = useState<StatusFilter>('active');
  const [busy, setBusy] = useState(false);
  const [actionMessage, setActionMessage] = useState<ActionMessage | null>(null);

  const buildQuery = useCallback((s: StatusFilter): string => {
    const params = new URLSearchParams();
    if (s === 'active') params.set('include_revoked', 'false');
    if (s === 'revoked') {
      // Server has no "revoked-only" filter; we include revoked and
      // filter client-side. Cheap because token volume is low.
    }
    if (s === 'expiring') {
      params.set('include_revoked', 'false');
      params.set('expires_within_days', '7');
    }
    params.set('limit', '500');
    return params.toString();
  }, []);

  const load = useCallback(
    async (s: StatusFilter) => {
      setState((prev) => ({ ...prev, loading: true }));
      try {
        const res = await api.get<{ tokens: AdminTokenRow[] }>(
          `/api/admin/tokens?${buildQuery(s)}`,
        );
        setState({ loading: false, tokens: res.tokens });
      } catch (err) {
        setState({ loading: false, error: apiMessage(err) });
      }
    },
    [buildQuery],
  );

  useEffect(() => {
    void load(status);
  }, [load, status]);

  const filtered = useMemo(() => {
    if (!state.tokens) return [];
    let rows = state.tokens;
    if (status === 'revoked') rows = rows.filter((t) => t.revoked_at !== null);
    const q = filterText.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((t) =>
      [t.label, t.user_email, t.user_name ?? '', t.app_slug, t.app_name]
        .join(' ')
        .toLowerCase()
        .includes(q),
    );
  }, [state.tokens, status, filterText]);

  async function revokeOne(row: AdminTokenRow) {
    if (
      !confirm(
        `Revoke "${row.label}" (${row.user_email}'s token on ${row.app_slug})? The client using it loses access immediately.`,
      )
    )
      return;
    await runAction({
      do: () => api.delete(`/api/admin/tokens/${row.id}`),
      onSuccess: () => `Revoked "${row.label}".`,
      errorPrefix: "Couldn't revoke",
      setBusy,
      setMessage: setActionMessage,
      reload: () => load(status),
    });
  }

  async function bulkRevoke(userId: string, userEmail: string) {
    if (
      !confirm(
        `Revoke EVERY active token belonging to ${userEmail} across all apps? This is a one-shot offboarding action.`,
      )
    )
      return;
    await runAction({
      do: () =>
        api.post<{ ok: true; revoked_count: number }>(`/api/admin/users/${userId}/revoke-tokens`),
      onSuccess: (res) =>
        `Revoked ${res.revoked_count} token${res.revoked_count === 1 ? '' : 's'} for ${userEmail}.`,
      errorPrefix: "Couldn't revoke",
      setBusy,
      setMessage: setActionMessage,
      reload: () => load(status),
    });
  }

  if (state.loading && !state.tokens) return <PageSkeleton rows={6} />;

  return (
    <div className="animate-fade-in">
      <PageHeader
        eyebrow="Admin"
        title="All tokens"
        description="Every per-app bearer token across every app. Revoke individual tokens or pull all of a user's access at once."
      />

      <AdminTabs />

      {state.error && <Banner tone="error">{state.error}</Banner>}
      {actionMessage && (
        <Banner tone={actionMessage.tone} onDismiss={() => setActionMessage(null)}>
          {actionMessage.text}
        </Banner>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_220px] gap-3 mb-5">
        <div className="relative">
          <IconSearch
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-faint pointer-events-none"
          />
          <input
            type="search"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            placeholder="Filter by label, user, or app…"
            aria-label="Filter tokens"
            className={`${inputClass} pl-9`}
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as StatusFilter)}
          className={inputClass}
          aria-label="Status filter"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {filtered.length === 0 ? (
        <Card>
          <CardHeader
            title="No tokens match"
            description={
              filterText
                ? `Nothing matches "${filterText}" with the current status filter.`
                : 'Nothing matches the current status filter.'
            }
          />
        </Card>
      ) : (
        <Card>
          <CardHeader
            title={`${filtered.length} token${filtered.length === 1 ? '' : 's'}`}
            description={statusDescription(status)}
          />
          <ul className="divide-y divide-line">
            {filtered.map((t) => (
              <li key={t.id} className="px-5 py-3 text-sm">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Mono className="font-medium">{t.label}</Mono>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-soft text-muted font-medium">
                        <IconPlug size={11} className="inline mr-1" />
                        {t.app_slug}
                      </span>
                      {t.revoked_at && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-danger-soft text-danger font-medium uppercase tracking-wide">
                          revoked · {t.revoked_reason}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted">
                      {t.user_name ?? t.user_email}
                      {t.user_name && ` · ${t.user_email}`} ·{' '}
                      {t.last_used_at ? `last used ${relTime(t.last_used_at)}` : 'never used'} ·{' '}
                      {t.revoked_at
                        ? `revoked ${relTime(t.revoked_at)}`
                        : `expires ${relTime(t.expires_at)}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {!t.revoked_at && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void revokeOne(t)}
                          disabled={busy}
                          className="!text-danger hover:!bg-danger-soft"
                        >
                          <IconTrash size={13} />
                          Revoke
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void bulkRevoke(t.user_id, t.user_email)}
                          disabled={busy}
                          title={`Revoke every active token for ${t.user_email}`}
                        >
                          Revoke all for user
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
